import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bot, Plus, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { listProcessAgentOptions } from '@/api/processes-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ProvenanceAttribution } from '@/components/work4me/ProvenanceAttribution';
import { Work4MeAgentCreate } from './Work4MeAgentCreate';

export function Work4MeAgentList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [showNewAgentDialog, setShowNewAgentDialog] = useState(false);
  const agentOptionsQuery = useQuery({
    queryKey: ['process-agent-options'],
    queryFn: listProcessAgentOptions,
  });

  const filteredAgents = useMemo(() => {
    const items = agentOptionsQuery.data?.items ?? [];
    const term = search.trim().toLowerCase();
    if (!term) {
      return items;
    }
    return items.filter((agent) =>
      agent.name.toLowerCase().includes(term)
      || (agent.description ?? '').toLowerCase().includes(term)
      || agent.id.toString().includes(term)
    );
  }, [search, agentOptionsQuery.data]);

  return (
    <div className="-mx-4 py-2">
      <div className="px-4 py-3 border-b bg-muted/20">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-light tracking-wide">Agents</h2>
            <div className="flex items-center gap-2 flex-wrap justify-end">
            <div className="relative w-[240px] sm:w-[280px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search agents..."
                className="h-9 pl-10"
              />
            </div>
            <Button onClick={() => setShowNewAgentDialog(true)} className="gap-2 h-9 px-3">
              <Plus className="h-4 w-4" />
              Agent
            </Button>
          </div>
          </div>
          <p className="text-muted-foreground">
            View enabled agents available to runtime and processes in this workspace.
          </p>
        </div>
      </div>

      {agentOptionsQuery.isLoading ? (
        <div className="px-4 py-6 text-sm text-muted-foreground">Loading agents...</div>
      ) : null}
      {agentOptionsQuery.isError ? (
        <div className="px-4 py-6 text-sm text-destructive">Unable to load agents.</div>
      ) : null}
      {!agentOptionsQuery.isLoading && !agentOptionsQuery.isError && filteredAgents.length === 0 ? (
        <div className="px-4 py-6 text-sm text-muted-foreground">
          {search.trim() ? 'No agents match your search.' : 'Nothing here yet — automations you create appear here.'}
        </div>
      ) : null}

      {!agentOptionsQuery.isLoading && !agentOptionsQuery.isError && filteredAgents.length > 0 ? (
        <ul className="divide-y">
          {filteredAgents.map((agent) => (
            <li
              key={agent.id}
              className="flex items-center gap-2 px-4 py-3 hover:bg-muted/30 transition-colors cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/work4me/agents/${agent.id}`)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigate(`/work4me/agents/${agent.id}`);
                }
              }}
              aria-label={`Open ${agent.name}`}
            >
              <Bot className="h-4 w-4 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">{agent.name}</p>
                  <Badge variant="outline">{agent.ownerScope}</Badge>
                </div>
                <p className="text-xs text-muted-foreground truncate">{agent.description ?? 'No description'}</p>
                <ProvenanceAttribution lastChange={agent.lastChange} />
              </div>
              <Badge variant="secondary" className="text-[10px]">ID {agent.id}</Badge>
            </li>
          ))}
        </ul>
      ) : null}

      <Work4MeAgentCreate
        open={showNewAgentDialog}
        onOpenChange={setShowNewAgentDialog}
        onCreated={(agentId) => navigate(`/work4me/agents/${agentId}`)}
      />
    </div>
  );
}

